import { Component, DestroyRef, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { API, PlanningDTO } from '../../../../services/api/api';
import { BookingEditorService } from '../../../../services/booking-editor/booking-editor.service';
import { Permission } from '../../../../services/api/permission';
import { UserService } from '../../../../services/user.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { ErrorService } from '../../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../../../services/util.service';
import { RoutingID } from '../../../../routes/sb-routing-enum';
import { NavigationService } from '@ngx-seatback/utils';
import { QueryParamService } from '../../../../services/lists/query-params';
import { PermissionService } from '../../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-planning',
  templateUrl: './planning.component.html',
  styleUrls: ['./planning.component.scss'],
  standalone: false,
})
export class PlanningComponent implements OnInit {
  submitted: number;
  planning: PlanningDTO;
  showBookingTitle: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    public bookingEditorService: BookingEditorService,
    public userStatus: UserService,
    private modal: ModalService,
    private errorHandler: ErrorService,
    public translate: TranslateService,
    public util: UtilService,
    public navService: NavigationService,
    public queryParamService: QueryParamService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.submitted = -1;
    this.planning = undefined;
    this.showBookingTitle = true;
    this.activatedRoute.params.subscribe((params: Params) => {
      this.bookingEditorService.planningId = params.planningID;
    });
  }

  ngOnInit() {
    this.refreshFromServer();
  }

  refreshFromServer() {
    if (this.queryParamService.getQueryParam('submission')) {
      this.submitted = this.queryParamService.getQueryParam('submission');
    }

    if (this.bookingEditorService.planningId) {
      this.api
        .ds_cm_Planning_retrieveById(this.bookingEditorService.planningId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe((pPlanning: PlanningDTO) => {
          this.planning = pPlanning;
          this.bookingEditorService.readonly = this.resolveReadOnlyStatus();
          this.bookingEditorService.customer = this.planning.forCustomer ? this.planning.forCustomer : null;
        });
    } else {
      this.bookingEditorService.readonly = true;
      this.showBookingTitle = false;
    }
  }

  resolveReadOnlyStatus(): boolean {
    return this.permissionService.hasPermissions([Permission.planning_edit]) === false;
  }

  submitPlanning(planning: PlanningDTO) {
    this.bookingEditorService.loading = true;
    this.api
      .ds_cm_Planning_submitById(planning?.id, {
        name: planning?.name,
        description: this.translate.instant('lang.planning.completePlanning'),
        forCustomer: planning?.forCustomer?.id,
        deleteOtherPlannings: false, // TODO check delete other plannings
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.bookingEditorService.loading = false;
        })
      )
      .subscribe({
        next: () => {
          this.navService.navigateToPoint(RoutingID.plannings);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  submitPlanningModal(planning: PlanningDTO) {
    this.bookingEditorService.checkPlanning().then(res => {
      if (res) {
        this.modal
          .confirm({
            title: this.translate.instant('lang.bookingEditor.modal.submitPlanningHeadline', { planningName: planning.name }),
            message: this.translate.instant('lang.bookingEditor.modal.submitPlanning', { planningName: planning.name }),
            yes: this.translate.instant('lang.globals.yes'),
            no: this.translate.instant('lang.globals.no'),
          })
          .then((result: boolean) => {
            if (result) {
              this.submitPlanning(planning);
            }
          });
      } else {
        this.modal
          .modal(
            {
              title: this.translate.instant('lang.bookingEditor.validation.planningIncomplete'),
              modalOptions: { windowClass: 'M' },
            },
            {
              templateId: 'planningIncompleteModal',
            }
          )
          .then(() => {})
          .catch(() => {});
      }
    });
  }

  acceptPlanning(planning: PlanningDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.bookingEditor.modal.acceptPlanningHeadline', { planningName: planning.name }),
        message: this.translate.instant('lang.bookingEditor.modal.acceptPlanningWithoutDelete', { planningName: planning.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.bookingEditorService.loading = true;
          this.api
            .ds_cm_Planning_acceptById(planning.id, this.submitted)
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              take(1),
              finalize(() => {
                this.bookingEditorService.loading = false;
              })
            )
            .subscribe({
              next: () => {
                this.navService.navigateToPoint(RoutingID.partialPlanning, [{ id: 'planningID', value: planning?.id }]);
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  rejectPlanning(planning: PlanningDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.bookingEditor.modal.rejectPlanningHeadline'),
        message: this.translate.instant('lang.bookingEditor.modal.rejectPlanning'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .ds_cm_Planning_withdrawSubmissionById(planning.id, this.submitted)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {
                this.navService.navigateToPoint(RoutingID.partialPlanning, [{ id: 'planningID', value: planning?.id }]);
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }
}
